import { z } from "zod";
import { GUEST_ROLES, WORKSPACE_ROLES } from "../roles.js";

export const BoardId = z.string().uuid();
export const BoardTitle = z.string().trim().min(1).max(255);

export const CreateBoard = z.object({
  parentId: BoardId,
  title: BoardTitle.default("Nuevo tablero"),
});
export type CreateBoard = z.infer<typeof CreateBoard>;

export const UpdateBoard = z.object({
  title: BoardTitle.optional(),
  /** Staff only: make the board (and everything inside) visible only to me, or back to the whole team. */
  private: z.boolean().optional(),
  icon: z.string().max(64).nullable().optional(),
  iconColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullable()
    .optional(),
  background: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullable()
    .optional(),
});
export type UpdateBoard = z.infer<typeof UpdateBoard>;

export const MoveBoard = z.object({ parentId: BoardId });

export const CreateBoardInvite = z.object({
  email: z.string().trim().toLowerCase().email().optional(),
  role: z.enum(GUEST_ROLES).default("viewer"),
  maxUses: z.number().int().positive().nullable().default(1),
  expiresInDays: z.number().int().min(1).max(90).default(14),
});
export type CreateBoardInvite = z.infer<typeof CreateBoardInvite>;

export const SetBoardMemberRole = z.object({ role: z.enum(GUEST_ROLES) });

export const CreateWorkspaceInvite = z.object({
  email: z.string().trim().toLowerCase().email(),
  role: z.enum(WORKSPACE_ROLES).exclude(["owner"]).default("member"),
});
export type CreateWorkspaceInvite = z.infer<typeof CreateWorkspaceInvite>;

export const BoardSummary = z.object({
  id: BoardId,
  parentId: BoardId.nullable(),
  title: z.string(),
  icon: z.string().nullable(),
  iconColor: z.string().nullable(),
  background: z.string().nullable(),
  cardCount: z.number().int(),
  updatedAt: z.string(),
  createdAt: z.string(),
  /** Set when the board itself is private; the owner's user id. */
  privateOwnerId: z.string().nullable(),
  /** True in a children list when the viewer may not open this (private) board; title/icon are blanked. */
  locked: z.boolean().optional(),
});
export type BoardSummary = z.infer<typeof BoardSummary>;
